import { EASE_OUT } from './easing';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import MuxVideo from '../MuxVideo';
import ReelScrubber from './ReelScrubber';
import { noteReelDismissed } from '../../reelTelemetry';
import { playOpen, playClose } from './sfx';

interface ReelPlayerProps {
  playbackId: string;
  onReady: () => void;
  /** reports theatre mode so the shell can clear the stage (hide the nav) */
  onTheater?: (open: boolean) => void;
  /**
   * Flips true while the preloader's sheet still covers the whole page — the
   * one moment the reel can be moved with nobody watching. This is NOT the
   * handover: the handover is most of a second too late. See the rewind below.
   */
  rewindCue?: boolean;
}

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const smoothstep = (t: number) => t * t * (3 - 2 * t);
/**
 * The reference's mid-flight accent, verbatim from their vertex shader:
 * `(smoothstep(p) - p)` is zero at both ends and bumps to ±0.096 in between,
 * so anything scaled by it only exists while the morph is in motion.
 */
const flightBump = (p: number) => smoothstep(p) - p;

/** how much scrolling the morph is spread over, as a fraction of the viewport */
export const MORPH_DISTANCE = 0.75;

/**
 * Every state of the player is a 16:9 frame, whatever shape the hero slot
 * happens to be. The slot is 4:3 on a phone and can be close to square on a
 * narrow desktop window, and a popup that inherited that read as a bug.
 */
const REEL_ASPECT = 16 / 9;

/**
 * The showreel lives in a single fixed element for the life of the page: it
 * starts as the hero frame and morphs into a corner player, and it is never
 * remounted, so playback and audio survive the trip.
 *
 * Rendered outside the section tree on purpose: a transformed ancestor would
 * become its containing block and defeat `position: fixed`.
 */
const ReelPlayer: React.FC<ReelPlayerProps> = ({ playbackId, onReady, onTheater, rewindCue = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const fadeRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const primed = useRef(false);
  const [muted, setMuted] = useState(true);
  // Theatre-only, and deliberately not the same thing as `dismissed`. It has to
  // live up here because MuxVideo's retry handlers key off the `paused` prop;
  // pausing the element directly is undone by the next canplay.
  const [heldPaused, setHeldPaused] = useState(false);
  // Theatre chrome — transport, sound, close — shares one clock so they appear
  // and leave together. Hover reveals on a mouse, a tap reveals on touch, and
  // stillness takes it all away again. Paused or mid-drag it stays put.
  const [chromeAwake, setChromeAwake] = useState(true);
  // Nothing fades before the visitor has arrived. Landing straight into a hero
  // whose sound control had already timed out would read as a missing button,
  // not as restraint, so the clock only starts once they move.
  const armed = useRef(false);
  const chromeTimer = useRef<number | null>(null);
  const chromeHeld = useRef(false);
  const awakeRef = useRef(true);
  awakeRef.current = chromeAwake;
  /** the pointer that opened the current click, so touch and mouse can differ */
  const lastPointer = useRef<string>('mouse');
  const [docked, setDocked] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Hero and theatre carry the transport; the docked popup is a thumbnail you
  // click to open and carries nothing. `docked && !expanded` is exactly that
  // popup, so this names every other state.
  const chromed = expanded || !docked;
  const expandedRef = useRef(false);
  expandedRef.current = expanded;
  // The visitor closed the floating player. Only the docked popup can be
  // dismissed: at the top of the page this element *is* the hero, and hiding
  // it there would leave a hole where the showreel should be. Scrolling back
  // up therefore brings it home rather than leaving the hero empty.
  const [dismissed, setDismissed] = useState(false);
  const dismissedRef = useRef(false);
  dismissedRef.current = dismissed;

  /**
   * The element's animated layout box. The morph drives these rather than a
   * uniform `scale` because the hero slot and the popup are different shapes:
   * scale can only keep the hero's aspect, and a non-uniform scale would
   * squash the video. Driving width/height lets `object-cover` re-crop, so the
   * frame genuinely turns landscape on the way down.
   */
  const bLeft = useMotionValue(0);
  const bTop = useMotionValue(0);
  const bWidth = useMotionValue(0);
  const bHeight = useMotionValue(0);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  // corner radius follows the frame: the hero's big curve would look cartoonish
  // on a 280px popup, and the popup's would look mean on the hero
  const radius = useMotionValue(36);
  // mid-flight bank, driven by the morph progress (see flightBump)
  const flightRot = useMotionValue(0);
  // user-dragged offset, applied on top of the scroll-driven position
  const dragX = useMotionValue(0);
  const dragY = useMotionValue(0);
  // How far through the morph we are, 0 at home in the hero slot and 1 fully
  // docked. The drag offset is scaled by it, so a popup that was dragged still
  // lands exactly in its slot when you scroll back up instead of arriving
  // shifted by however far it was moved. Scroll down again and the offset
  // comes back, so the placement is not lost either.
  const morph = useMotionValue(0);
  const drag = useRef({ active: false, moved: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });

  // Underdamped on purpose: the reference runs its motion on second-order
  // dynamics with ζ≈0.7 / f≈2.2Hz (read from their bundle), which is
  // stiffness (2π·2.2)² ≈ 190 and damping 2ζ√k ≈ 19 — it overshoots and
  // settles rather than gliding to a stop.
  const cfg = { stiffness: 190, damping: 19.5, mass: 1 };
  const sx = useSpring(x, cfg);
  const sy = useSpring(y, cfg);
  const sRadius = useSpring(radius, cfg);
  // final position = scroll-driven morph + the dragged offset, faded out as
  // the reel returns home
  const finalX = useTransform([sx, dragX, morph], ([a, b, m]: number[]) => a + b * m);
  const finalY = useTransform([sy, dragY, morph], ([a, b, m]: number[]) => a + b * m);

  // Their morph carries no squash and no bow — those were my invention and are
  // what made this read as a different animation. It is a straight glide plus a
  // small mid-flight rotation, so that is all this does now.
  const sFlightRot = useSpring(flightRot, { stiffness: 170, damping: 22 });
  const sbLeft = useSpring(bLeft, cfg);
  const sbTop = useSpring(bTop, cfg);
  const sbWidth = useSpring(bWidth, cfg);
  const sbHeight = useSpring(bHeight, cfg);

  const measure = useCallback(() => {
    const slot = document.getElementById('hero-reel-slot');
    if (!slot) return;
    const r = slot.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (!vw || !vh || !r.width || !r.height) return;

    const isMobile = vw < 768;
    // the slot never moves in the document, so its scroll-0 position is fixed
    const heroLeft = r.left;
    const heroTop = r.top + window.scrollY;
    const heroW = r.width;
    const heroH = r.height;
    // matches the slot's own rounding in HeroSection
    const heroRadius = isMobile ? 24 : 36;

    if (expandedRef.current) {
      // 16:9 and centred, rather than the hero's shape blown up: on a narrow
      // window that shape is nearly square, and object-cover was throwing away
      // a third of the reel in the one view meant to show all of it.
      const pad = isMobile ? 14 : 34;
      const w = Math.min(vw - pad * 2, (vh - pad * 2) * REEL_ASPECT);
      const h = w / REEL_ASPECT;
      bLeft.set((vw - w) / 2);
      bTop.set((vh - h) / 2);
      bWidth.set(w);
      bHeight.set(h);
      radius.set(isMobile ? 20 : 28);
      x.set(0);
      y.set(0);
      flightRot.set(0);
      return;
    }

    // One popup at every width. This used to scale the hero box uniformly,
    // which meant the popup inherited whatever shape the slot happened to be:
    // 16:9 on a wide desktop, 4:3 on a phone, and very nearly square on a tall
    // narrow window. Morphing the box instead lets object-cover re-crop the
    // frame, so the same landscape player lands in the corner everywhere.
    //
    // The width still tracks the viewport: 280px is right on a desktop but
    // would cover most of a 390px phone, which is what made it sit on top of
    // the work-card titles before.
    const miniW = Math.min(280, vw * 0.47);
    const miniH = miniW / REEL_ASPECT;
    const margin = isMobile ? 12 : 22;
    // the hero slot's position on screen right now, not in the document
    const heroViewportTop = heroTop - window.scrollY;

    const p = Math.min(1, Math.max(0, window.scrollY / (vh * MORPH_DISTANCE)));
    const e = easeInOutCubic(p);

    const w = lerp(heroW, miniW, e);
    bLeft.set(lerp(heroLeft, vw - miniW - margin, e));
    bTop.set(lerp(heroViewportTop, vh - miniH - margin, e));
    bWidth.set(w);
    bHeight.set(lerp(heroH, miniH, e));
    radius.set(lerp(heroRadius, 16, e));

    // The piece I was missing: they push the frame sideways through the
    // transition by up to a tenth of its own width, on a cosine that is zero
    // at both ends. It swings out and comes back rather than travelling in a
    // straight line, which is most of why theirs reads as alive.
    x.set(0.1 * w * (0.5 - 0.5 * Math.cos(p * Math.PI * 2)));
    y.set(0);

    // A rotation that exists only mid-morph: (smoothstep(p) - p) * -0.5 rad.
    // That peaks at just ~2.8 degrees, so the frame banks subtly and lands flat.
    flightRot.set(flightBump(p) * -0.5 * (180 / Math.PI));

    setDocked(p > 0.6);
    // back at the hero slot: the popup is not a popup any more, so a dismissal
    // of it no longer means anything
    if (p < 0.05 && dismissedRef.current) setDismissed(false);
    morph.set(e);

    if (!primed.current) {
      primed.current = true;
      // land on the first measurement instead of springing in from zero
      [
        [sbLeft, bLeft],
        [sbTop, bTop],
        [sbWidth, bWidth],
        [sbHeight, bHeight],
        [sx, x],
        [sy, y],
        [sRadius, radius],
      ].forEach(([spring, source]: any) => {
        const v = source.get();
        spring.jump ? spring.jump(v) : spring.set(v);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [x, y, radius, sx, sy, sRadius, bLeft, bTop, bWidth, bHeight, morph]);

  useEffect(() => {
    measure();
    const onScroll = () => measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure);

    // Everything the frame knows about itself comes from this one box, so watch
    // the box rather than guess when it will be ready. This replaces a
    // setTimeout(measure, 300) that was a hope, not a signal: it fired once,
    // early, and if the slot still had no usable size the frame stayed unplaced
    // until a scroll or a window resize happened to come along. A slot that
    // starts life without a real size is not hypothetical — a tab opened in the
    // background, a restored session, a print or prerender pass — and none of
    // those reliably produce a window resize event.
    //
    // The observer fires once on observe with the current size and again on
    // every change, and measure() is idempotent, so the extra call costs
    // nothing: priming only ever runs once.
    const slot = document.getElementById('hero-reel-slot');
    const ro = slot ? new ResizeObserver(() => measure()) : null;
    ro?.observe(slot!);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [measure]);

  /**
   * The reel is playing before anyone can see it.
   *
   * Autoplay gets going about 1.2s in and the film would otherwise be seconds
   * deep by the time the curtain clears, so the visitor's first frame was well
   * into the cut and everything after — the white end included — arrived early.
   *
   * This used to be one seek fired on the handover, and that was wrong in a way
   * measurement caught and reasoning did not: the sheet wipes UPWARD and the
   * reel sits at the top of the page, so the reel is the LAST thing uncovered.
   * The handover fires 110ms (desktop) to 206ms (mobile) AFTER the reel is in
   * full view, and the seek was landing in plain sight — 2.4s of film, then a
   * snap back to zero. Reported from the field as "it starts, then jumps back
   * and starts again".
   *
   * So the seek rides the curtain instead, on a cue the preloader sends while
   * the page is still completely hidden. And because a cue is still a moment in
   * time — and one whose margin depends on the hero's layout, an easing curve
   * and a duration that all live in other files — the clock is also held near
   * the first frame for as long as the page is covered. That is the part that
   * does not rely on any of them: however late the cue lands, and however slow
   * the seek, the clock can only ever step back a fraction of a second rather
   * than the whole opening.
   *
   * Held with a seek and never a pause. Pausing reads through to ReelScrubber,
   * which polls v.paused every frame and would paint a play button over the
   * hero on every load — the exact thing that went wrong the last time this
   * file was touched.
   */
  const rewound = useRef(false);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    if (rewindCue) {
      if (rewound.current) return;
      rewound.current = true;
      // Anything still at zero needs no help, and seeking a reel that has not
      // started could interrupt the first play.
      if (v.currentTime > 0.05) v.currentTime = 0;
      return;
    }

    // Still fully covered. timeupdate rather than a timer: the browser tells us
    // when the clock has moved, so there is nothing here to tune or to rot.
    const pin = () => {
      if (v.currentTime > 0.3) v.currentTime = 0;
    };
    v.addEventListener('timeupdate', pin);
    return () => v.removeEventListener('timeupdate', pin);
  }, [rewindCue]);

  useEffect(() => {
    const v = videoRef.current;
    let fired = false;
    const fire = () => {
      if (!fired) {
        fired = true;
        onReady();
      }
    };
    v?.addEventListener('loadeddata', fire);
    v?.addEventListener('canplay', fire);
    const t = setTimeout(fire, 3500);
    return () => {
      v?.removeEventListener('loadeddata', fire);
      v?.removeEventListener('canplay', fire);
      clearTimeout(t);
    };
  }, [onReady]);

  const fadeTo = (target: number, thenMute: boolean) => {
    const v = videoRef.current;
    if (!v) return;
    if (fadeRef.current) clearInterval(fadeRef.current);
    if (!thenMute) {
      v.muted = false;
      v.volume = 0;
    }
    fadeRef.current = setInterval(() => {
      const next = thenMute ? v.volume - 0.08 : v.volume + 0.06;
      v.volume = Math.max(0, Math.min(1, next));
      if ((thenMute && v.volume <= 0) || (!thenMute && v.volume >= 1)) {
        if (thenMute) v.muted = true;
        if (fadeRef.current) clearInterval(fadeRef.current);
      }
    }, 30);
    setMuted(thenMute);
    v.play().catch(() => {});
  };

  const toggleSound = (e: React.MouseEvent) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    fadeTo(0, !(v.muted || v.volume === 0) ? true : false);
  };

  const wakeChrome = useCallback((hold?: boolean) => {
    if (hold !== undefined) chromeHeld.current = hold;
    // Only touch React when it would actually change something: this runs on
    // every pointermove across the picture.
    if (!awakeRef.current) setChromeAwake(true);
    if (chromeTimer.current) window.clearTimeout(chromeTimer.current);
    if (!armed.current) return;
    chromeTimer.current = window.setTimeout(() => {
      if (!chromeHeld.current) setChromeAwake(false);
    }, 2600);
  }, []);

  // First move, touch or scroll anywhere: the visitor is here, start the clock.
  useEffect(() => {
    const arm = () => {
      if (armed.current) return;
      armed.current = true;
      wakeChrome();
    };
    const opts = { passive: true, once: true } as AddEventListenerOptions;
    window.addEventListener('pointermove', arm, opts);
    window.addEventListener('touchstart', arm, opts);
    window.addEventListener('scroll', arm, opts);
    return () => {
      window.removeEventListener('pointermove', arm);
      window.removeEventListener('touchstart', arm);
      window.removeEventListener('scroll', arm);
    };
  }, [wakeChrome]);

  const sleepChrome = useCallback(() => {
    if (chromeTimer.current) window.clearTimeout(chromeTimer.current);
    setChromeAwake(false);
  }, []);

  /** Acts immediately from the gesture, and records the intent so the retries
   *  in MuxVideo leave it alone. */
  const togglePlayback = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const hold = !v.paused;
    setHeldPaused(hold);
    if (hold) v.pause();
    else void v.play().catch(() => {});
  }, []);

  const expand = () => {
    setExpanded(true);
    onTheater?.(true);
    playOpen();
    const v = videoRef.current;
    if (v && (v.muted || v.volume === 0)) fadeTo(1, false);
  };

  const collapse = useCallback(() => {
    // The transport belongs to theatre, and so does its paused state: the
    // docked reel is ambient and should never come back stopped.
    setHeldPaused(false);
    setExpanded(false);
    onTheater?.(false);
    playClose();
  }, [onTheater]);

  // Manual drag rather than framer's `drag`, which would fight the springs
  // driving the scroll morph. Only the docked popup is movable.
  const onPointerDown = (e: React.PointerEvent) => {
    lastPointer.current = e.pointerType || 'mouse';
    if (!docked || expanded) return;
    drag.current = {
      active: true,
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      baseX: dragX.get(),
      baseY: dragY.get(),
    };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    // Hover is what reveals the chrome on a desktop; touch has its own path in
    // the click handler, and a finger sliding across should not count as hover.
    if (chromed && e.pointerType !== 'touch') wakeChrome();
    const d = drag.current;
    if (!d.active) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (Math.abs(dx) + Math.abs(dy) > 4) d.moved = true;
    dragX.set(d.baseX + dx);
    dragY.set(d.baseY + dy);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current;
    d.active = false;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
  };

  const onSurfaceClick = () => {
    // In theatre the picture is the pause target, the way every player behaves.
    // The transport and the corner controls stop propagation, so this only ever
    // fires on the picture itself. It is a real gesture, so play() here is not
    // spending an autoplay allowance.
    if (chromed) {
      // What every player does: on touch the picture is a light switch for the
      // chrome, and play/pause belongs to the button. With a mouse the chrome
      // is already following the cursor, so the picture can pause instead.
      if (lastPointer.current === 'touch') {
        if (chromeAwake) sleepChrome();
        else wakeChrome();
      } else {
        wakeChrome();
        togglePlayback();
      }
      return;
    }
    // a drag should not also count as a click
    if (drag.current.moved) {
      drag.current.moved = false;
      return;
    }
    if (docked) expand();
  };

  // A paused reel is a reel someone is looking at; nothing fades under them.
  useEffect(() => {
    if (!chromed) return;
    if (heldPaused) {
      chromeHeld.current = true;
      setChromeAwake(true);
    } else {
      chromeHeld.current = false;
      wakeChrome();
    }
  }, [heldPaused, chromed, wakeChrome]);

  useEffect(() => {
    if (chromed) wakeChrome(false);
  }, [chromed, wakeChrome]);

  useEffect(() => {
    measure();
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') collapse();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded, measure, collapse]);

  /**
   * One definition, two homes. In the hero and in theatre it is the last item
   * of the centred transport row; on the docked popup it is back in the corner
   * beside expand and dismiss, at the size that fits a thumbnail.
   */
  const renderSound = (big: boolean) => (
            <motion.button
              onClick={toggleSound}
              // keep a press on a control from starting a drag of the popup
              onPointerDown={(e) => e.stopPropagation()}
              // Its own hint. Without one, closest('[data-cursor]') walks up to
              // the reel frame and this button claimed to be "DRAG / OPEN".
              data-cursor={muted ? 'UNMUTE' : 'MUTE'}
              // No visible text, so the name is the action rather than the state;
              // aria-pressed carries the state.
              aria-label={muted ? 'Turn sound on' : 'Mute sound'}
              aria-pressed={!muted}
              whileHover={{ scale: 1.07 }}
              whileTap={{ scale: 0.92 }}
              transition={{ type: 'spring', stiffness: 420, damping: 26 }}
              // compact once docked: a full-size control would swamp the popup
              // Theatre sizes it to the transport's play button rather than to
              // the popup's: they bookend the same row, and a 32 against a 40
              // read as two unrelated controls that happened to line up.
              className={`relative flex items-center justify-center rounded-full backdrop-blur-md border border-white/30 bg-black/50 text-white transition-colors duration-300 hover:bg-white hover:text-ink ${
                big ? 'h-12 w-12 md:h-14 md:w-14' : 'h-8 w-8'
              }`}
            >
              <span
                className={`relative flex items-center justify-center ${
                  big ? 'w-5 h-5 md:w-[22px] md:h-[22px]' : 'w-4 h-4'
                }`}
              >
                {/* muted */}
                <motion.svg
                  viewBox="0 0 24 24"
                  className="absolute inset-0 w-full h-full"
                  aria-hidden="true"
                  initial={false}
                  animate={{ opacity: muted ? 1 : 0, scale: muted ? 1 : 0.55 }}
                  transition={{ duration: 0.22, ease: EASE_OUT }}
                >
                  <path d="M11.2 4.6 6.6 8.6H3.3v6.8h3.3l4.6 4z" fill="currentColor" />
                  <path
                    d="M15.4 9.6 20 14.4M20 9.6l-4.6 4.8"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </motion.svg>

                {/* playing */}
                <motion.span
                  className="absolute inset-0 flex items-center justify-center gap-[2.5px]"
                  initial={false}
                  animate={{ opacity: muted ? 0 : 1, scale: muted ? 0.55 : 1 }}
                  transition={{ duration: 0.22, ease: EASE_OUT }}
                >
                  {[0.5, 1, 0.7, 0.9].map((h, i) => (
                    <motion.span
                      key={i}
                      className="w-[2.5px] bg-current rounded-full"
                      animate={muted ? { height: 3 } : { height: [3, 14 * h, 3] }}
                      transition={
                        muted
                          ? { duration: 0.2 }
                          : { repeat: Infinity, duration: 0.9, delay: i * 0.12 }
                      }
                    />
                  ))}
                </motion.span>
              </span>
            </motion.button>
  );


  return (
    <>
      <AnimatePresence>
        {expanded && (
          <motion.div
            className="fixed inset-0 z-[69] bg-ink/85 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: EASE_OUT }}
            onClick={collapse}
            data-cursor="CLOSE"
          />
        )}
      </AnimatePresence>

      <motion.div
        style={{
          position: 'fixed',
          left: sbLeft,
          top: sbTop,
          width: sbWidth,
          height: sbHeight,
          x: finalX,
          y: finalY,
          rotate: sFlightRot,
          transformOrigin: '0 0',
          willChange: 'transform',
          // NOT gated on the measurement. Hiding the frame until it had been
          // placed removed 0.0126 of CLS and broke the reel: play() is called
          // while the frame is still hidden, Chrome accepts it — paused false,
          // readyState 4 — but an unrendered video never advances its clock and
          // does not resume when the frame is shown again. It sticks at t=0
          // permanently, and MuxVideo cannot rescue it because its retry guard
          // is `if (video.paused)`, which is false in that state. Reproduced
          // 3/3 on a cold cache at 8x CPU / 1.5Mbps; intermittent on real
          // hardware, which is exactly how it reached production unnoticed.
          // The CLS it bought was already scoring 1.0. Do not reintroduce this
          // without keeping the <video> rendered throughout.
          borderRadius: sRadius,
          pointerEvents: dismissed ? 'none' : 'auto',
        }}
        animate={{ opacity: dismissed ? 0 : 1 }}
        transition={{ duration: 0.4, ease: EASE_OUT }}
        data-surface="dark"
        className={`z-[70] overflow-hidden bg-card ${
          expanded
            ? 'shadow-[0_40px_120px_-20px_rgba(0,0,0,0.7)]'
            : docked
            ? 'shadow-[0_18px_50px_-12px_rgba(11,12,16,0.45)]'
            : ''
        }`}
        onClick={onSurfaceClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        data-cursor={!expanded && docked ? 'DRAG / OPEN' : undefined}
      >
        <MuxVideo
          paused={dismissed || heldPaused}
          poster="/reel-poster.webp"
          playbackId={playbackId}
          videoRef={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
        />

        <motion.div
          className="absolute inset-0 pointer-events-none"
          animate={{ opacity: docked ? 0 : 1 }}
          transition={{ duration: 0.4 }}
          style={{
            background:
              'linear-gradient(to bottom, rgba(6,7,12,0.6) 0%, rgba(6,7,12,0.05) 30%, rgba(6,7,12,0.10) 52%, rgba(6,7,12,0.62) 82%, rgba(6,7,12,0.88) 100%)',
          }}
        />

        {/* corner marks, as on the reference's reel frame */}
        {['left-3 top-3', 'right-3 top-3', 'left-3 bottom-3', 'right-3 bottom-3'].map((pos, i) => (
          <motion.span
            key={i}
            className={`absolute ${pos} text-white/40 font-grotesk text-sm select-none pointer-events-none`}
            animate={{ opacity: expanded ? 1 : 0 }}
            transition={{ duration: 0.4, delay: expanded ? 0.3 : 0 }}
          >
            +
          </motion.span>
        ))}

        {/* the frame's box is animated, not scaled, so the controls keep their
            own size throughout and only need to shed padding once docked */}
        <motion.div
          // Fades with the transport: in theatre every control shares one
          // clock, so sound does not sit there alone after the rest has gone.
          // Top-right on the popup. Theatre mode keeps `docked` true and puts
          // its own Close control top-right, so the row only moves up while
          // the popup is a popup; expanded, it stays out of that corner.
          // In theatre these sit on the transport's own baseline rather than in
          // the corner, so sound reads as the last control in that row instead
          // of a stray button colliding with the duration.
          // Only the popup's corner now: in the hero and in theatre the sound
          // control has moved into the transport row so all three can be
          // centred as one group.
          className="absolute z-30 right-2 top-2 flex items-center gap-1.5"
        >
          {/* Two marks that swap: a speaker with a cross while muted, the
              equalizer once the sound is on. The muted state used to be the
              same bars lying flat under a strike, which at 16px read as four
              dots and a line rather than as "sound off". A speaker is the one
              shape everyone already knows.

              Both live in the same box and cross-fade, so the swap is one
              gesture rather than a hard cut. Nothing here is a word, so the
              button stays the same circle at every size. */}
          {!chromed && renderSound(false)}

          {/* explicit expand control, so opening the reel isn't a hidden gesture */}
          {docked && !expanded && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                expand();
              }}
              onPointerDown={(e) => e.stopPropagation()}
              data-cursor="EXPAND"
              aria-label="Expand showreel"
              className="h-8 w-8 flex items-center justify-center rounded-full backdrop-blur-md border border-white/30 bg-black/50 text-white transition-colors duration-300 hover:bg-white hover:text-ink"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15 3h6v6" />
                <path d="M9 21H3v-6" />
                <path d="M21 3l-7.5 7.5" />
                <path d="M3 21l7.5-7.5" />
              </svg>
            </button>
          )}

          {/* Dismiss. A floating video nobody asked for should be closeable,
              and closing it stops the stream rather than just hiding it. */}
          {docked && !expanded && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                noteReelDismissed();
                setDismissed(true);
                playClose();
              }}
              onPointerDown={(e) => e.stopPropagation()}
              data-cursor="DISMISS"
              aria-label="Dismiss showreel"
              className="h-8 w-8 flex items-center justify-center rounded-full backdrop-blur-md border border-white/30 bg-black/50 text-white transition-colors duration-300 hover:bg-white hover:text-ink"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
                <path d="M4 4l16 16M20 4L4 20" />
              </svg>
            </button>
          )}
        </motion.div>

        <motion.div
          className={`absolute pointer-events-none ${
            docked ? 'left-3 bottom-2.5' : 'left-4 md:left-8 bottom-4 md:bottom-7'
          }`}
          animate={{ opacity: docked && !expanded ? 1 : 0 }}
          transition={{ duration: 0.35 }}
        >
          <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-white/85">Showreel</span>
        </motion.div>

        {/* pointer-events-none on the wrapper, not just the button: this box
            occupies the popup's top-right corner even while its button is
            invisible, and it was swallowing clicks meant for the controls
            that now live there. The button re-enables its own events when
            expanded; a child may do that inside a pointer-events-none parent. */}
        <div className="absolute right-4 top-4 pointer-events-none">
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              collapse();
            }}
            data-cursor="CLOSE"
            // Goes with the rest. It always comes straight back on a move or a
            // tap, and the backdrop still closes the theatre, so nobody is
            // stranded in here by a control that faded.
            animate={{ opacity: expanded && chromeAwake ? 1 : 0 }}
            transition={{ duration: 0.32, ease: EASE_OUT }}
            style={{ pointerEvents: expanded && chromeAwake ? 'auto' : 'none' }}
            className="h-10 px-5 rounded-full bg-white/12 backdrop-blur-md border border-white/30 text-white font-grotesk text-[10px] uppercase tracking-[0.2em] hover:bg-white hover:text-ink transition-colors duration-300"
          >
            Close
          </motion.button>
        </div>

        {/* Theatre only. The docked player is a thumbnail you click to open, and
            a scrub bar across it would be a few pixels tall. */}
        <ReelScrubber
          videoRef={videoRef}
          active={chromed}
          visible={chromeAwake}
          onToggle={togglePlayback}
          onActivity={wakeChrome}
          raised={!docked}
          trailing={renderSound(true)}
        />
      </motion.div>
    </>
  );
};

export default ReelPlayer;
